import AxisCameraStream from './AxisCameraStream';
import FisheyeSphere from './FisheyeSphere';
import { ResizableElement } from '/js/components/html_components/ResizableElement';

class CameraStreamWindow
{
  constructor()
  {
    this.container = null;
    this.canvas = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;

    this.fisheye_sphere = null;
    this.video_texture = null;
    this.axis_camera_stream = null;

    this.visible = false;
    this.initialized = false;

    // Orbit camera state
    this.current_tilt = -45;
    this.current_orientation = 90;
    this.horizontal_speed = 0;
    this.vertical_speed = 0;
    this.is_dragging = false;
    this.last_mouse_x = 0;
    this.last_mouse_y = 0;

    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onWheel = this._onWheel.bind(this);
    this._onCanvasResize = this._onCanvasResize.bind(this);

    this.camera_fov = 60;
    this.min_fov = 40;
    this.max_fov = 90;
  }

  start()
  {
    this.container = document.querySelector('.camera-stream-window');
    this.canvas = document.getElementById('camera-stream-canvas');

    if (!this.container || !this.canvas)
    {
      console.warn('[CameraStreamWindow] DOM elements not found');
      return;
    }

    const resizable = new ResizableElement(
      '.camera-stream-window',
      '.camera-stream-window__header',
      '.camera-stream-resize-handle'
    );
    resizable.start();

    this._resizeObserver = new ResizeObserver(this._onCanvasResize);
    this._resizeObserver.observe(this.container);

    this._bindCloseButton();
    this._injectToolbarButton();
  }

  _bindCloseButton()
  {
    const close_button = this.container.querySelector('.camera-stream-window__header-close');
    if (!close_button) return;

    close_button.addEventListener('click', (e) =>
    {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
    });
  }

  _injectToolbarButton()
  {
    const toolbar = document.querySelector('.cesium-viewer-toolbar');
    if (!toolbar)
    {
      console.warn('[CameraStreamWindow] .cesium-viewer-toolbar not found, cannot inject button');
      return;
    }

    const btn = document.createElement('button');
    btn.className = 'cesium-button cesium-toolbar-button camera-stream-toolbar-button';
    btn.type = 'button';
    btn.title = 'Camera Stream';
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>';

    btn.addEventListener('click', () => this.toggle());

    toolbar.appendChild(btn);
    this.toolbar_button = btn;
  }

  _initRenderer()
  {
    if (this.initialized) return;

    const body = this.container.querySelector('.camera-stream-window__body');
    const width = body.clientWidth || 640;
    const height = body.clientHeight || 450;

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false,
      alpha: true
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);
    this.renderer.setClearColor(new THREE.Color('#000000'), 1);

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(this.camera_fov, width / height, 0.1, 200);
    this.camera.position.z = 10;
    this.camera.updateProjectionMatrix();

    const fisheye_fov = 180;
    const use_upper_hemisphere = false;

    this.fisheye_sphere = new FisheyeSphere(fisheye_fov, use_upper_hemisphere, height);
    this.scene.add(this.fisheye_sphere);

    const video_element = this.container.querySelector('.camera-stream-video-1');
    if (video_element)
    {
      video_element.src = '/videos/demo.mp4';
      video_element.loop = true;
      video_element.play();

      this.video_texture = new THREE.VideoTexture(video_element);
      this.fisheye_sphere.set_texture(this.video_texture);
    }

    this._updateCameraRotation();
    this._bindCanvasEvents();

    this.initialized = true;
  }

  _bindCanvasEvents()
  {
    this.canvas.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup', this._onMouseUp);
    this.canvas.addEventListener('wheel', this._onWheel, { passive: false });
  }

  _onMouseDown(e)
  {
    if (e.button !== 0) return;
    this.is_dragging = true;
    this.last_mouse_x = e.clientX;
    this.last_mouse_y = e.clientY;
    e.preventDefault();
  }

  _onMouseMove(e)
  {
    if (!this.is_dragging) return;

    const dx = e.clientX - this.last_mouse_x;
    const dy = e.clientY - this.last_mouse_y;
    this.last_mouse_x = e.clientX;
    this.last_mouse_y = e.clientY;

    this.horizontal_speed += dx * 0.3;
    this.vertical_speed -= dy * 0.3;
  }

  _onMouseUp()
  {
    this.is_dragging = false;
  }

  _onWheel(e)
  {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 2 : -2;
    this.camera_fov = THREE.Math.clamp(this.camera_fov + delta, this.min_fov, this.max_fov);
    if (this.camera)
    {
      this.camera.fov = this.camera_fov;
      this.camera.updateProjectionMatrix();
    }
  }

  _updateCameraRotation()
  {
    this.current_tilt = THREE.Math.clamp(this.current_tilt, -90, 90);

    const up = new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3(-1, 0, 0);

    const orientQuat = new THREE.Quaternion().setFromAxisAngle(up, THREE.Math.degToRad(this.current_orientation));
    const tiltQuat = new THREE.Quaternion().setFromAxisAngle(right, THREE.Math.degToRad(this.current_tilt));
    const rotation = orientQuat.multiply(tiltQuat);

    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(rotation);

    this.camera.position.copy(forward.multiplyScalar(0.001));
    this.camera.quaternion.copy(rotation);
  }

  _onCanvasResize()
  {
    if (!this.renderer || !this.visible) return;

    const body = this.container.querySelector('.camera-stream-window__body');
    if (!body) return;

    const width = body.clientWidth;
    const height = body.clientHeight;

    if (width > 0 && height > 0)
    {
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
  }

  show()
  {
    if (!this.container) return;

    this.container.classList.remove('hidden');
    this.visible = true;

    if (!this.initialized)
    {
      this._initRenderer();
    }

    this._onCanvasResize();
  }

  hide()
  {
    if (!this.container) return;

    this.container.classList.add('hidden');
    this.visible = false;
  }

  toggle()
  {
    if (this.visible)
    {
      this.hide();
    }
    else
    {
      this.show();
    }
  }

  /**
   * Set up an RTSP camera stream via AxisCameraStream.
   * Call after start() and show() to begin streaming.
   */
  start_stream(user, password, ip, port, ws_port)
  {
    const video_element = this.container.querySelector('.camera-stream-video-1');
    if (!video_element) return;

    if (this.axis_camera_stream)
    {
      this.axis_camera_stream.stop();
    }

    this.axis_camera_stream = new AxisCameraStream(video_element, user, password, ip, port, ws_port);
    this.axis_camera_stream.start_video_pipeline();

    if (!this.video_texture && this.fisheye_sphere)
    {
      this.video_texture = new THREE.VideoTexture(video_element);
      this.fisheye_sphere.set_texture(this.video_texture);
    }
  }

  /**
   * Set a local video source (file path or URL) for testing without RTSP.
   */
  set_video_source(src)
  {
    const video_element = this.container.querySelector('.camera-stream-video-1');
    if (!video_element) return;

    video_element.src = src;
    video_element.play();

    if (this.fisheye_sphere && !this.video_texture)
    {
      this.video_texture = new THREE.VideoTexture(video_element);
      this.fisheye_sphere.set_texture(this.video_texture);
    }
  }

  update()
  {
    if (!this.visible || !this.initialized) return;

    // Apply inertia / rotation
    this.current_orientation = (this.current_orientation - this.horizontal_speed) % 360;
    this.current_tilt += this.vertical_speed;
    this._updateCameraRotation();

    // Decay
    this.horizontal_speed *= 0.9;
    this.vertical_speed *= 0.9;

    // Render
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
  }
}

export default CameraStreamWindow;
